import { DeleteOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Empty, Flex, Input, List, Tag, Typography, message } from 'antd';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionDrawer } from '../../components/ActionDrawer';
import { StockLocationPicker } from '../../components/StockLocationPicker';
import { ApiError } from '../../lib/api-client';
import {
  cancelJobPartsRequest,
  listJobPartsRequests,
  requestJobParts,
  searchParts,
  type JobCard,
  type JobPartsRequest,
  type PartChoice,
} from './api';
import { addDraftLine, canRequestParts, hasPartsWaiting, isValidQty, type DraftLine } from './partsDraft';

const { Text } = Typography;

/** Parts asked for against this job, and the drawer that asks for more. */
export function JobParts({ job }: { job: JobCard }): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [note, setNote] = useState('');
  const [locationUid, setLocationUid] = useState<string | undefined>(undefined);
  const [cancelling, setCancelling] = useState<JobPartsRequest | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebounced(term.trim());
    }, 300);
    return () => {
      clearTimeout(handle);
    };
  }, [term]);

  const { data: requests } = useQuery({
    queryKey: ['job-parts', job.uid],
    queryFn: () => listJobPartsRequests(job.uid),
  });
  const { data: found = [], isFetching } = useQuery({
    queryKey: ['job-parts-search', debounced],
    queryFn: () => searchParts(debounced),
    enabled: open && debounced.length >= 2,
  });

  const send = useMutation({
    mutationFn: () =>
      requestJobParts({
        jobCardUid: job.uid,
        ...(locationUid !== undefined ? { locationUid } : {}),
        lines: lines.map((l) => ({ skuUid: l.skuUid, qty: l.qty })),
        ...(note.trim().length > 0 ? { note: note.trim() } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['job-parts', job.uid] });
      void queryClient.invalidateQueries({ queryKey: ['job-card', job.uid] });
      setOpen(false);
      setLines([]);
      setNote('');
      setTerm('');
      setLocationUid(undefined);
      void message.success(t('workshop.partsSent'));
    },
    // Closed and invoiced jobs are refused by the API in plain words; show them.
    onError: (e: unknown) => {
      void message.error(e instanceof ApiError ? e.message : t('workshop.partsFailed'));
    },
  });

  const cancel = useMutation({
    mutationFn: () => cancelJobPartsRequest(cancelling?.uid ?? '', { reason: cancelReason.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['job-parts', job.uid] });
      void queryClient.invalidateQueries({ queryKey: ['job-card', job.uid] });
      setCancelling(null);
      setCancelReason('');
      void message.success(t('workshop.partsCancelled'));
    },
    onError: (e: unknown) => {
      void message.error(e instanceof ApiError ? e.message : t('workshop.cancelPartsFailed'));
    },
  });

  const add = (part: PartChoice): void => {
    setLines((current) => addDraftLine(current, { skuUid: part.uid, skuCode: part.skuCode, skuName: part.name }));
  };

  const canRequest = job.closedAt === null && canRequestParts(job.status);
  const canSend = lines.length > 0 && lines.every((l) => isValidQty(l.qty));

  return (
    <>
      <Card
        title={t('workshop.parts')}
        extra={
          canRequest && (
            <Button
              type="primary"
              onClick={() => {
                setOpen(true);
              }}
            >
              {t('workshop.requestParts')}
            </Button>
          )
        }
      >
        {(requests?.items ?? []).length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('workshop.noPartsRequested')} />
        ) : (
          <Flex vertical gap={16}>
            {(requests?.items ?? []).map((req) => (
              <Flex key={req.uid} vertical gap={4}>
                <Flex justify="space-between" align="center" gap={8} wrap>
                  <Text strong>{req.requestNo}</Text>
                  <Tag color={req.status === 'BACKORDER' ? 'error' : undefined}>
                    {t(`parts.status.${req.status}`)}
                  </Tag>
                </Flex>
                {req.lines.map((line) => (
                  <Flex key={line.uid} justify="space-between" gap={8}>
                    <Text>
                      {line.skuName} <Text type="secondary">{line.skuCode}</Text>
                    </Text>
                    <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
                      {t('workshop.askedGiven', { asked: line.qtyRequested, given: line.qtyIssued })}
                    </Text>
                  </Flex>
                ))}
                {req.note !== null && <Text type="secondary">{req.note}</Text>}
                {hasPartsWaiting(req.status) && job.closedAt === null && (
                  <Button
                    size="large"
                    onClick={() => {
                      setCancelReason('');
                      setCancelling(req);
                    }}
                  >
                    {t('workshop.cancelWaiting')}
                  </Button>
                )}
              </Flex>
            ))}
          </Flex>
        )}
      </Card>

      <ActionDrawer
        open={cancelling !== null}
        title={t('workshop.cancelWaiting')}
        submitLabel={t('workshop.cancelWaiting')}
        submitting={cancel.isPending}
        submitDisabled={cancelReason.trim().length < 3}
        danger
        onClose={() => {
          setCancelling(null);
        }}
        onSubmit={() => {
          cancel.mutate();
        }}
        consequence={t('workshop.cancelWaitingConsequence')}
      >
        <Flex vertical gap={8}>
          <Text>{t('workshop.cancelWaitingFor', { requestNo: cancelling?.requestNo ?? '' })}</Text>
          <Input.TextArea
            rows={3}
            value={cancelReason}
            maxLength={500}
            placeholder={t('workshop.cancelReasonPlaceholder')}
            onChange={(e) => {
              setCancelReason(e.target.value);
            }}
          />
        </Flex>
      </ActionDrawer>

      <ActionDrawer
        open={open}
        title={t('workshop.requestParts')}
        submitLabel={t('workshop.sendToWarehouse')}
        submitting={send.isPending}
        submitDisabled={!canSend}
        onClose={() => {
          setOpen(false);
        }}
        onSubmit={() => {
          send.mutate();
        }}
        consequence={t('workshop.partsConsequence', { jobNo: job.jobNo })}
      >
        <Flex vertical gap={16}>
          <StockLocationPicker value={locationUid} onChange={setLocationUid} label={t('stockLocation.putAsideAt')} />
          <Input.Search
            size="large"
            allowClear
            value={term}
            loading={isFetching}
            placeholder={t('workshop.searchParts')}
            onChange={(e) => {
              setTerm(e.target.value);
            }}
          />
          {debounced.length >= 2 && !isFetching && found.length === 0 && (
            <Text type="secondary">{t('workshop.noPartsFound', { term: debounced })}</Text>
          )}
          <List
            dataSource={found}
            renderItem={(part) => (
              <List.Item
                key={part.uid}
                actions={[
                  <Button
                    key="add"
                    size="large"
                    onClick={() => {
                      add(part);
                    }}
                  >
                    {t('workshop.addPart')}
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={part.name}
                  description={
                    <Flex vertical>
                      <Text type="secondary">{part.skuCode}</Text>
                      <Text type={Number(part.totalAvailable) > 0 ? undefined : 'danger'}>
                        {t('workshop.onShelf', { qty: part.totalAvailable, uom: part.uom })}
                        {' · '}
                        {part.autocountQty === null
                          ? t('parts.noAutocount')
                          : t('workshop.autocountSays', { qty: part.autocountQty })}
                      </Text>
                    </Flex>
                  }
                />
              </List.Item>
            )}
          />

          {lines.length > 0 && (
            <Flex vertical gap={8}>
              <Text strong>{t('workshop.partsOnRequest')}</Text>
              {lines.map((line) => (
                <Flex key={line.skuUid} gap={8} align="center">
                  <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                    <Text ellipsis>{line.skuName}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {line.skuCode}
                    </Text>
                  </Flex>
                  <Input
                    size="large"
                    inputMode="decimal"
                    style={{ width: 96 }}
                    value={line.qty}
                    status={isValidQty(line.qty) ? undefined : 'error'}
                    aria-label={t('workshop.qtyFor', { name: line.skuName })}
                    onChange={(e) => {
                      setLines((current) =>
                        current.map((l) => (l.skuUid === line.skuUid ? { ...l, qty: e.target.value.trim() } : l)),
                      );
                    }}
                  />
                  <Button
                    size="large"
                    icon={<DeleteOutlined />}
                    aria-label={t('workshop.removePart', { name: line.skuName })}
                    onClick={() => {
                      setLines((current) => current.filter((l) => l.skuUid !== line.skuUid));
                    }}
                  />
                </Flex>
              ))}
              {lines.some((l) => !isValidQty(l.qty)) && (
                <Text type="danger">{t('workshop.qtyHelp')}</Text>
              )}
            </Flex>
          )}

          <Input.TextArea
            rows={2}
            value={note}
            maxLength={500}
            placeholder={t('workshop.partsNote')}
            onChange={(e) => {
              setNote(e.target.value);
            }}
          />
        </Flex>
      </ActionDrawer>
    </>
  );
}
